// External libraries
import { ApolloLink, Observable } from 'apollo-link';
import type { ErrorResponse } from 'apollo-link-error';
import { onError } from 'apollo-link-error';

// Internal modules
import { logger } from '@/lib/logger';
import { camelToSnakeCase } from '@/lib/utils';
import {
  getApiCorrelationId,
  handleGraphQLErrors,
  handleNetworkError,
} from '../utils';
import { CI } from '@/lib/customerInteractions';

// This link is for logging the result of the API call to CAL and FPTI.
// Those logs are used for debugging, analytics, and populating
// monitoring dashboards.
const link = new ApolloLink((operation, forward) => {
  const operationName = camelToSnakeCase(operation.operationName);
  logger.info(`api_request_${operationName}`);

  return new Observable(observer => {
    const subscription = forward(operation).subscribe({
      complete: observer.complete.bind(observer),
      error: observer.error.bind(observer),
      next: response => {
        // Errors from user input will be logged from the `errorLink` that is
        // defined below and concatenated with this link. We need this check
        // because this block of code will be run when there are non-network
        // errors returned in the API response.
        const { errors } = response;
        const hasError = Array.isArray(errors) && Boolean(errors.length);
        if (!hasError) {
          logger.info(`api_response_status_200_${operationName}`, {
            apiCorrelationId: getApiCorrelationId({ response }),
          });
        }

        observer.next(response);
      },
    });

    return () => subscription?.unsubscribe?.();
  });
});

export const errorLink = onError((data: ErrorResponse): void => {
  const { graphQLErrors, networkError, operation, response } = data;
  const operationName = camelToSnakeCase(operation.operationName);
  const apiCorrelationId = getApiCorrelationId({ networkError, response });

  // Set current interaction to FCI if there was a failure
  const hasFailure = graphQLErrors?.some(
    errorData => errorData.extensions?.class === 'FAILURE',
  );
  if (hasFailure) {
    CI.fci(CI.ErrorReason.Server);
  }

  /**
   * From https://www.apollographql.com/docs/react/data/error-handling/#graphql-errors
   *   "These are errors related to the server-side execution of a GraphQL operation. They include:
   *     - Syntax errors (e.g., a query was malformed)
   *     - Validation errors (e.g., a query included a schema field that doesn't exist)
   *     - Resolver errors (e.g., an error occurred while attempting to populate a query field)"
   */
  handleGraphQLErrors({ apiCorrelationId, graphQLErrors, operationName });
  /**
   * From https://www.apollographql.com/docs/react/data/error-handling/#network-errors
   *   "These are errors encountered while attempting to communicate with your GraphQL server,
   *    usually resulting in a 4xx or 5xx response status code (and no data)."
   */
  handleNetworkError({ apiCorrelationId, networkError, operationName });
});

export const loggerLink = link.concat(errorLink);
